import type { IntakeDraft, IntakeIssuer, IntakeSource } from './aiIntake'

/**
 * Which upload stands as the listing's proof of ownership.
 *
 * The form insists on one file that shows the land details are genuine. The
 * AI says, for every upload, who issued it and whether it is an authoritative
 * document that identifies the property; this picks the strongest of those
 * for each draft so the agent is not asked for a file they already gave.
 *
 * Strength is the authority behind the document first (a land office over a
 * lawyer or valuer, those over a developer), then the kind of document (the
 * title itself over a bill that merely cites it). Sessions saved before the
 * AI reported an issuer fall back to the title documents alone.
 */

/** What the listing endpoint accepts as proof: an image or a PDF. */
export const PROOF_FILE = /\.(pdf|jpe?g|png|webp)$/i

const ISSUER_RANK: Record<IntakeIssuer, number> = {
    government: 0,
    licensed_professional: 1,
    developer: 2,
    unknown: 3,
    agent_or_owner: 4,
}

const TYPE_RANK: Record<string, number> = {
    land_title: 0,
    strata_title: 0,
    title_search: 1,
    quit_rent_bill: 2,
    assessment_bill: 2,
    sale_and_purchase_agreement: 3,
    valuation_report: 4,
    official_letter: 5,
    // A surveyor's or land office's plan of the land: supporting evidence, behind anything that names the title.
    title_plan: 6,
    survey_plan: 6,
}

/** The documents that count on their own, for answers that carry no proof flag. */
const TITLE_DOCUMENTS = ['land_title', 'strata_title']

const rank = (s: IntakeSource) => [ISSUER_RANK[s.issuer ?? 'unknown'], TYPE_RANK[s.documentType] ?? 9] as const

export const isProofSource = (s: IntakeSource) => (s.proofOfOwnership === undefined ? TITLE_DOCUMENTS.includes(s.documentType) : s.proofOfOwnership) && PROOF_FILE.test(s.file)

/** Loosely: the AI cites files by name, sometimes without the extension. */
const cites = (draft: IntakeDraft, file: string) => draft.sourceReferences.some((ref) => ref.includes(file) || file.includes(ref))

/**
 * The proof sources for one draft, strongest first. A document is the
 * draft's when the draft cites it, or when it is the only draft, since a
 * single listing's uploads are all about that listing.
 */
export function proofCandidates(sources: IntakeSource[], draft: IntakeDraft, listingCount: number): IntakeSource[] {
    return sources
        .filter((s) => isProofSource(s) && (listingCount === 1 || cites(draft, s.file)))
        .sort((a, b) => {
            const [ia, ta] = rank(a)
            const [ib, tb] = rank(b)
            return ia - ib || ta - tb
        })
}

export const pickProof = (sources: IntakeSource[], draft: IntakeDraft, listingCount: number): IntakeSource | undefined => proofCandidates(sources, draft, listingCount)[0]
